import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Clock, Globe, Users, ChevronDown, ChevronUp, HelpCircle, Filter, Check, X, User, Mail, Phone, Building } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { formatDate } from "../utils/dateUtils";
import { COURSE_GROUPS, LANGUAGES, BAB_TYPES, getCoursePrice, getCourseGroupBySlug, UCPLUS_NOTE, UCPLUS_LIVE_COURSE_IDS } from "../data/courseGroups";
import { COURSE_DETAILS } from "../data/courseDetails";
import { toast } from "sonner";
import UcplusCourseList from "../components/UcplusCourseList";
import CourseFactsSections, { CourseStructuredData, courseFaqList } from "../components/kurser/CourseFactsSections";
import ExamSyllabusSection from "../components/kurser/ExamSyllabusSection";
import { loadTrustpilotInvite } from "../utils/thirdParty";
import CourseProcessSection from "../components/kurser/CourseProcessSection";
import { LOCATION_PAGES } from "../data/locationPages";
import WaitlistForm from "../components/kurser/WaitlistForm";

// Course slugs that have a live UCplus hold integration (see backend COURSE_CONFIG)
const LIVE_UCPLUS_COURSES = UCPLUS_LIVE_COURSE_IDS;

const CourseGroupDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Kursusgruppen kommer fra statisk data og hentes nu synkront. Lå den i en
  // useState sat fra en useEffect, var den null under build-tidens prerendering,
  // og hele siden blev erstattet af en spinner i den HTML, en klient uden
  // JavaScript får. Kun holdlisten kommer fra API'et og har stadig loading.
  const courseGroup = getCourseGroupBySlug(slug);
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showMore, setShowMore] = useState(false);
  
  // Registration modal state
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationSession, setRegistrationSession] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    name: '',
    email: '',
    phone: '',
    cpr_number: '',
    company: '',
    comments: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [languageFilter, setLanguageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Default FAQ items
  const defaultFaq = [
    {
      question: "Hvad er inkluderet i kursusprisen?",
      answer: "Kursusprisen inkluderer undervisning, kursusmaterialer og certificering ved bestået kursus. Forplejning er ikke inkluderet, medmindre andet fremgår."
    },
    {
      question: "Hvordan foregår tilmelding?",
      answer: "Du tilmelder dig ved at klikke på 'Tilmeld' ud for den ønskede kursusdato. Udfyld tilmeldingsformularen, og du modtager en bekræftelse på email."
    },
    {
      question: "Hvilke sprog udbydes kurset på?",
      answer: courseGroup?.hasLanguageVariants
        ? "Kurset udbydes på dansk, engelsk, arabisk, ukrainsk og persisk. Se filtrene ovenfor for at finde kurser på dit foretrukne sprog."
        : "Kurset udbydes kun på dansk."
    },
    {
      question: "Kan jeg deltage som ledig?",
      answer: "Ja, som ledig kan du deltage via 6-ugers selvvalgt uddannelse. Kontakt dit jobcenter for at høre om dine muligheder."
    },
    {
      question: "Hvornår modtager jeg mit certifikat?",
      answer: "Certifikatet udstedes umiddelbart efter bestået kursus. Du kan downloade det digitalt fra din profil."
    }
  ];

  const faqItems = courseGroup ? courseFaqList(courseGroup, sessions, defaultFaq) : defaultFaq;

  useEffect(() => {
    if (courseGroup) {
      fetchSessions(courseGroup);
    } else {
      setLoading(false);
    }
    // courseGroup er den samme reference, så længe slug er det samme (find() på
    // det statiske katalog), så den kan trygt stå i afhængighedslisten.
  }, [slug, courseGroup]);

  useEffect(() => {
    applyFilters();
  }, [sessions, languageFilter, typeFilter]);

  const fetchSessions = async (group) => {
    try {
      // Fetch sessions for this course group
      const res = await axios.get(`${API}/course-groups/${group.slug}/sessions`);
      setSessions(res.data || []);
    } catch (error) {
      console.error("Fejl ved hentning af kurser:", error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...sessions];

    if (languageFilter !== "all") {
      filtered = filtered.filter((s) => s.language === languageFilter);
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter((s) => s.babType === typeFilter);
    }

    setFilteredSessions(filtered);
  };

  const getAvailableSpots = (session) => {
    return session.total_spots - session.booked_spots;
  };

  const openRegistrationModal = (session) => {
    setRegistrationSession(session);
    setRegistrationForm({
      name: '',
      email: '',
      phone: '',
      cpr_number: '',
      company: '',
      comments: ''
    });
    setShowRegistrationModal(true);
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!registrationForm.name || !registrationForm.email || !registrationForm.phone || !registrationForm.cpr_number) {
      toast.error("Udfyld venligst alle påkrævede felter");
      return;
    }
    const cprDigits = (registrationForm.cpr_number || '').replace(/\D/g, '');
    if (cprDigits.length !== 10) {
      toast.error("CPR-nummer skal være 10 cifre (DDMMÅÅ-XXXX)");
      return;
    }

    setSubmitting(true);
    
    try {
      // Submit registration to backend
      await axios.post(`${API}/public/registrations`, {
        session_id: registrationSession.id,
        course_name: registrationSession.name || courseGroup.title,
        session_date: registrationSession.date,
        name: registrationForm.name,
        email: registrationForm.email,
        phone: registrationForm.phone,
        cpr_number: cprDigits,
        company: registrationForm.company,
        comments: registrationForm.comments,
        price: registrationSession.price
      });
      
      toast.success("Tilmelding modtaget! Vi sender en bekræftelse til din email.");
      setShowRegistrationModal(false);

      // Trustpilot: invite the customer to review after registration.
      // Scriptet hentes først her, så det ikke ligger på hver sidevisning.
      try {
        await loadTrustpilotInvite();
        if (window.tp) {
          window.tp('createInvitation', {
            recipientEmail: registrationForm.email,
            recipientName: registrationForm.name,
            referenceId: `${registrationSession.id}-${Date.now()}`,
            source: 'InvitationScript',
          });
        }
      } catch (e) {
        console.error('Trustpilot invitation error:', e);
      }
      
      // Refresh sessions to update available spots
      if (courseGroup) {
        fetchSessions(courseGroup);
      }
    } catch (error) {
      console.error("Fejl ved tilmelding:", error);
      toast.error("Kunne ikke gennemføre tilmelding. Prøv igen eller kontakt os.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!courseGroup) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 40%, 98%)' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-700 mb-4">Kursus ikke fundet</h2>
            <button
              onClick={() => navigate('/kurser')}
              className="text-[hsl(210,100%,45%)] hover:underline"
            >
              ← Tilbage til kursusoversigt
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero Section with Course Image */}
      <div className="relative">
        <div 
          className="h-64 md:h-96 bg-cover bg-center"
          style={{ backgroundImage: `url(${courseGroup.image})` }}
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(9,16,32,0.92) 0%, rgba(9,16,32,0.45) 45%, rgba(9,16,32,0.15) 100%)" }}></div>
        </div>
        
        {/* Back button */}
        <div className="absolute top-4 left-4">
          <button
            onClick={() => navigate('/kurser')}
            className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-slate-700 hover:bg-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage
          </button>
        </div>

        {/* Course Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="container mx-auto">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-3 leading-[1.05]">
              {courseGroup.title}
            </h1>
            {courseGroup.duration && (
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {courseGroup.duration}
                </span>
                {courseGroup.modules && (
                  <span>{courseGroup.modules} moduler</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Upcoming Sessions (internal DB). Hidden for live-UCplus courses which show UCplus holds below. */}
            {!LIVE_UCPLUS_COURSES.includes(courseGroup.slug) && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              {courseGroup.ucplusPartner && (
                <div
                  className="mb-4 rounded-lg border border-[hsl(210,100%,45%)]/20 bg-[hsl(210,100%,45%)]/5 px-4 py-3 text-sm text-slate-700"
                  data-testid="ucplus-note"
                >
                  {UCPLUS_NOTE}
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                  {courseGroup.selfStudy ? "Kommende prøvedatoer" : "Kommende kurser"}
                </h2>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <Filter className="h-4 w-4" />
                  Filtrér
                  {(languageFilter !== "all" || typeFilter !== "all") && (
                    <span className="w-2 h-2 bg-[hsl(210,100%,45%)] rounded-full"></span>
                  )}
                </button>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex flex-wrap gap-4">
                    {courseGroup.hasLanguageVariants && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Sprog</label>
                        <select
                          value={languageFilter}
                          onChange={(e) => setLanguageFilter(e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="all">Alle sprog</option>
                          {Object.entries(LANGUAGES).map(([code, lang]) => (
                            <option key={code} value={code}>{lang.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {courseGroup.hasTypeVariants && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        >
                          <option value="all">Alle typer</option>
                          {courseGroup.types.map((type) => (
                            <option key={type} value={type}>{BAB_TYPES[type]?.name || type}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {(languageFilter !== "all" || typeFilter !== "all") && (
                      <button
                        onClick={() => { setLanguageFilter("all"); setTypeFilter("all"); }}
                        className="self-end px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                      >
                        Nulstil filtre
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Sessions List */}
              {loading ? (
                <div className="text-center py-8 text-slate-500" data-testid="sessions-loading">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-[hsl(210,100%,45%)] border-r-transparent" />
                  <p className="mt-3">
                    Henter kommende {courseGroup.selfStudy ? "prøvedatoer" : "hold"} ...
                  </p>
                </div>
              ) : sessions.length === 0 ? (
                <WaitlistForm slug={courseGroup.slug} title={courseGroup.title} selfStudy={courseGroup.selfStudy} />
              ) : filteredSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>
                    {courseGroup.selfStudy
                      ? "Ingen kommende prøvedatoer matcher dine filtre. Ring til os på +45 55 29 28 28, så finder vi en dato."
                      : "Ingen kommende kurser matcher dine filtre."}
                  </p>
                  {(languageFilter !== "all" || typeFilter !== "all") && (
                    <button
                      onClick={() => { setLanguageFilter("all"); setTypeFilter("all"); }}
                      className="mt-2 text-[hsl(210,100%,45%)] hover:underline"
                    >
                      Vis alle kurser
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSessions.map((session) => {
                    const availableSpots = getAvailableSpots(session);
                    const isFull = availableSpots <= 0;
                    
                    return (
                      <div
                        key={session.id}
                        className={`flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg ${
                          isFull ? 'border-slate-200 bg-slate-50' : 'border-slate-200 hover:border-[hsl(210,100%,45%)]'
                        } transition-colors`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 md:mb-0">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              <span className="font-medium">{formatDate(session.date)}</span>
                            </div>
                            {session.time && (
                              <>
                                <span className="text-slate-400">•</span>
                                <span className="text-slate-600">{session.time}</span>
                              </>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm">
                            <span className="flex items-center gap-1 text-slate-500">
                              <MapPin className="h-3.5 w-3.5" />
                              {session.location}
                            </span>
                            
                            {session.languageName && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-slate-600">
                                <Globe className="h-3 w-3" />
                                {session.languageName}
                              </span>
                            )}
                            
                            {session.babType && (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                {BAB_TYPES[session.babType]?.name || session.babType}
                              </span>
                            )}
                            
                            <span className={`flex items-center gap-1 ${
                              isFull ? 'text-red-500' : availableSpots <= 3 ? 'text-amber-500' : 'text-green-600'
                            }`}>
                              <Users className="h-3.5 w-3.5" />
                              {isFull ? 'Fuldt booket' : `${availableSpots} pladser tilbage`}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 md:mt-0">
                          <span className="font-semibold text-[hsl(210,100%,45%)]">
                            {session.price?.toLocaleString('da-DK')} kr.
                          </span>
                          <button
                            onClick={() => !isFull && openRegistrationModal(session)}
                            disabled={isFull}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              isFull
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-[hsl(210,100%,45%)] text-white hover:bg-[hsl(210,100%,40%)]'
                            }`}
                            data-testid={`register-btn-${session.id}`}
                          >
                            {isFull ? 'Fuldt' : courseGroup.selfStudy ? 'Tilmeld prøve' : 'Tilmeld'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Live UCplus holds for configured courses (VB GU, Taxa) */}
            {LIVE_UCPLUS_COURSES.includes(courseGroup.slug) && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                {courseGroup.ucplusPartner && (
                  <div
                    className="mb-4 rounded-lg border border-[hsl(210,100%,45%)]/20 bg-[hsl(210,100%,45%)]/5 px-4 py-3 text-sm text-slate-700"
                    data-testid="ucplus-note"
                  >
                    {UCPLUS_NOTE}
                  </div>
                )}
                <UcplusCourseList
                  course={courseGroup.slug}
                  votraSessions={filteredSessions.map((s) => {
                    const spots = getAvailableSpots(s);
                    const teachingMap = { dag: "Dagsundervisning", aften: "Aftenundervisning", weekend: "Weekendundervisning" };
                    let deadline = "";
                    if (s.date) {
                      const d = new Date(s.date);
                      if (!isNaN(d.getTime())) {
                        d.setDate(d.getDate() - 1);
                        deadline = formatDate(d.toISOString().slice(0, 10));
                      }
                    }
                    return {
                      source: "votra",
                      id: s.id,
                      startDate: formatDate(s.date),
                      time: s.time,
                      teachingForm: teachingMap[(s.time_type || "").toLowerCase()] || "",
                      endDate: s.end_date ? formatDate(s.end_date) : "",
                      registrationDeadline: deadline,
                      location: s.location,
                      language: s.languageName || "",
                      availableSeats: spots,
                      isFull: spots <= 0,
                      price: s.price,
                      holdNummer: s.hold_nummer,
                      _raw: s,
                    };
                  })}
                  onVotraSignup={openRegistrationModal}
                />
              </div>
            )}

            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                Om kurset
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {courseGroup.description}
              </p>

              {COURSE_DETAILS[slug] && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowMore((v) => !v)}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(210,100%,45%)] hover:underline"
                    aria-expanded={showMore}
                    data-testid="course-readmore-toggle"
                  >
                    {showMore ? "Vis mindre" : "Læs mere om kurset"}
                    {showMore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>

                  {showMore && (
                    <div className="mt-5 space-y-6 border-t border-slate-100 pt-5" data-testid="course-readmore-content">
                      {COURSE_DETAILS[slug].intro && (
                        <p className="text-slate-600 leading-relaxed">{COURSE_DETAILS[slug].intro}</p>
                      )}
                      {COURSE_DETAILS[slug].learningOutcomes?.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-3">Det lærer du</h3>
                          <ul className="space-y-2.5">
                            {COURSE_DETAILS[slug].learningOutcomes.map((o, i) => (
                              <li key={i} className="flex items-start gap-3 text-slate-600">
                                <Check className="h-5 w-5 mt-0.5 flex-shrink-0 text-[hsl(210,100%,45%)]" />
                                <span className="leading-relaxed">{o}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {COURSE_DETAILS[slug].afterCourse?.length > 0 && (
                        <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4">
                          <h3 className="font-semibold text-slate-900 mb-3">Efter kurset kan du</h3>
                          <ul className="space-y-2.5">
                            {COURSE_DETAILS[slug].afterCourse.map((o, i) => (
                              <li key={i} className="flex items-start gap-3 text-slate-600">
                                <Check className="h-5 w-5 mt-0.5 flex-shrink-0 text-emerald-600" />
                                <span className="leading-relaxed">{o}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {COURSE_DETAILS[slug].targetAudience && (
                        <p className="text-sm text-slate-500">
                          <span className="font-semibold text-slate-700">Hvem er kurset for? </span>
                          {COURSE_DETAILS[slug].targetAudience}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Krav */}
            {((courseGroup.requirements && courseGroup.requirements.length > 0) || courseGroup.requirementsNote) && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-requirements-section">
                <h2 className="text-xl font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                  Krav
                </h2>
                {courseGroup.requirements && courseGroup.requirements.length > 0 && (
                  <ul className="space-y-3">
                    {courseGroup.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-600" data-testid={`course-requirement-${idx}`}>
                        <Check className="h-5 w-5 mt-0.5 flex-shrink-0 text-[hsl(210,100%,45%)]" />
                        <span className="leading-relaxed">{req}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {courseGroup.requirementsNote && (
                  <p
                    className={`text-slate-600 leading-relaxed ${courseGroup.requirements && courseGroup.requirements.length > 0 ? 'mt-4 text-sm text-slate-500' : ''}`}
                    data-testid="course-requirements-note"
                  >
                    {courseGroup.requirementsNote}
                  </p>
                )}
              </div>
            )}

            {/* Kursusindhold (for IT & AI courses with a content list) */}
            {courseGroup.content && courseGroup.content.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="course-content-section">
                <h2 className="text-xl font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                  Det lærer du på kurset
                </h2>
                <ul className="space-y-3">
                  {courseGroup.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-600">
                      <Check className="h-5 w-5 mt-0.5 flex-shrink-0 text-[hsl(210,100%,45%)]" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Available Languages/Types */}
            {(courseGroup.hasLanguageVariants || courseGroup.hasTypeVariants) && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                {courseGroup.hasLanguageVariants ? "Tilgængelige sprog" : "Tilgængelige typer"}
              </h2>
              
              {courseGroup.hasLanguageVariants ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(LANGUAGES).map(([code, lang]) => {
                    const price = getCoursePrice(courseGroup, code);
                    return (
                      <div 
                        key={code}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-[hsl(210,100%,45%)] transition-colors cursor-pointer"
                        onClick={() => setLanguageFilter(languageFilter === code ? "all" : code)}
                      >
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{lang.name}</span>
                        </div>
                        {courseGroup.slug !== 'vb-gu' && (
                          <span className="text-sm text-slate-500">{price.standard?.toLocaleString('da-DK')} kr.</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : courseGroup.hasTypeVariants ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {courseGroup.types.map((type) => {
                    const typeInfo = BAB_TYPES[type];
                    return (
                      <div 
                        key={type}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-[hsl(210,100%,45%)] transition-colors cursor-pointer"
                        onClick={() => setTypeFilter(typeFilter === type ? "all" : type)}
                      >
                        <div>
                          <span className="font-medium">{typeInfo?.name || type}</span>
                          {typeInfo?.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{typeInfo.description}</p>
                          )}
                          {typeInfo?.requirement && (
                            <p className="text-xs mt-1" data-testid={`bab-requirement-${type}`}>
                              <span className="font-medium text-slate-600">Krav: </span>
                              <span className="text-slate-500">{typeInfo.requirement}</span>
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-medium text-[hsl(210,100%,45%)]">
                          {typeInfo?.price?.toLocaleString('da-DK')} kr.
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            )}


            {/* Faktaboks, definition, godkendelse/prøve og myndighedskilder */}
            <CourseFactsSections group={courseGroup} sessions={sessions} />

            {/* Forløbet trin for trin, prøvens officielle pensum og kravene til tilladelsen */}
            <ExamSyllabusSection group={courseGroup} />

            {/* Forløbet trin for trin og kravene efter kurset (taxa, BAB, EVS) */}
            <CourseProcessSection group={courseGroup} />


            {/* FAQ Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                <HelpCircle className="h-5 w-5" />
                Ofte stillede spørgsmål
              </h2>
              
              <div className="space-y-2">
                {/* Svaret ligger ALTID i DOM'en og skjules kun visuelt. Før blev det
                    conditional-renderet, så hverken Google, AI-crawlerne eller
                    prerenderingen kunne læse svarene, selv om de stod i FAQPage-schemaet. */}
                {faqItems.map((faq, index) => {
                  const open = expandedFaq === index;
                  return (
                    <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedFaq(open ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                        aria-expanded={open}
                        aria-controls={`faq-answer-${index}`}
                        data-testid={`cg-faq-question-${index}`}
                      >
                        <span className="font-medium text-slate-700">{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </button>
                      <div
                        id={`faq-answer-${index}`}
                        className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                        data-testid={`cg-faq-answer-${index}`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 pb-4 text-slate-600">{faq.answer}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              {/* Price Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                  Priser
                </h3>
                
                {courseGroup.itPricing ? (
                  <div className="space-y-3" data-testid="it-pricing">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'hsl(210, 100%, 45%, 0.08)' }}>
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        Pris for ledige
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        Gratis*
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        *Gratis for dig som ledig, hvis kurset godkendes af dit jobcenter.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-600">
                        Pris for jobcenter
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        {courseGroup.itPricing.jobcenter?.toLocaleString('da-DK')} kr.
                      </div>
                      <p className="text-xs text-slate-500 mt-1">ekskl. moms</p>
                    </div>
                  </div>
                ) : courseGroup.slug === 'vb-gu' ? (
                  <div className="space-y-3" data-testid="vbgu-pricing">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'hsl(210, 100%, 45%, 0.08)' }}>
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        Deltagerpris
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        654 DKK
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-600">
                        Pris for ledige
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        3.765 DKK
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Jobcenter/kommune kan efter aftale betale kursusudgiften.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-600">
                        Pris udenfor AMU-målgruppe
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        3.765 DKK
                      </div>
                    </div>
                  </div>
                ) : courseGroup.hasLanguageVariants ? (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'hsl(210, 100%, 45%, 0.08)' }}>
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        Dansk
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'hsl(210, 100%, 45%)' }}>
                        {getCoursePrice(courseGroup, 'da').standard?.toLocaleString('da-DK')} kr.
                      </div>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'hsl(24, 94%, 50%, 0.08)' }}>
                      <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'hsl(24, 94%, 50%)' }}>
                        Inkl. sprogtillæg
                      </div>
                      <div className="text-2xl font-bold" style={{ color: 'hsl(24, 94%, 50%)' }}>
                        {getCoursePrice(courseGroup, 'en').standard?.toLocaleString('da-DK')} kr.
                      </div>
                    </div>
                  </div>
                ) : courseGroup.hasTypeVariants ? (
                  <div className="space-y-2">
                    {courseGroup.types.slice(0, 5).map((type) => {
                      const typeInfo = BAB_TYPES[type];
                      return (
                        <div key={type} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                          <span className="text-sm text-slate-600">{typeInfo?.name || type}</span>
                          <span className="font-semibold text-[hsl(210,100%,45%)]">
                            {typeInfo?.price?.toLocaleString('da-DK')} kr.
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'hsl(210, 100%, 45%, 0.08)' }}>
                    <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'hsl(210, 100%, 45%)' }}>
                      {courseGroup.category === 'vognmand' ? 'Kursuspris' : 'Jobcenter/A-kasse'}
                    </div>
                    <div className="text-2xl font-bold" style={{ color: 'hsl(210, 100%, 45%)' }}>
                      {getCoursePrice(courseGroup, 'da').standard?.toLocaleString('da-DK')} kr.
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Info */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                  Hurtig info
                </h3>
                <ul className="space-y-3 text-sm">
                  {courseGroup.duration && (
                    <li className="flex items-center gap-2 text-slate-600">
                      <Clock className="h-4 w-4 text-slate-400" />
                      Varighed: {courseGroup.duration}
                    </li>
                  )}
                  <li className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {courseGroup.selfStudy && courseGroup.examVenues?.length
                      ? `Prøvested: ${courseGroup.examVenues.join(" og ")}`
                      : `Lokation: ${courseGroup.location || "Ringsted"}`}
                  </li>
                  {courseGroup.hasLanguageVariants && (
                    <li className="flex items-center gap-2 text-slate-600">
                      <Globe className="h-4 w-4 text-slate-400" />
                      {Object.keys(LANGUAGES).length} sprogvarianter
                    </li>
                  )}
                </ul>
              </div>

              {/* Lokationer: de dybe lokationssider havde kun indgående links fra
                  /lokationer og /adresser. Googlebot vurderer intern linkning, så de
                  skal linkes fra de kursussider, de hører til. */}
              {(() => {
                const venues = (courseGroup.examVenues || [courseGroup.location]).filter(Boolean).join(" ").toLowerCase();
                const pages = LOCATION_PAGES.filter((l) => venues.includes(l.city.toLowerCase()));
                if (pages.length === 0) return null;
                return (
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="cg-locations">
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'hsl(222.2, 47.4%, 11.2%)' }}>
                      {courseGroup.selfStudy ? "Hvor aflægges prøven?" : "Hvor foregår det?"}
                    </h3>
                    <ul className="space-y-2.5 text-sm">
                      {pages.map((l) => (
                        <li key={l.slug}>
                          <Link
                            to={`/lokationer/${l.slug}`}
                            className="inline-flex items-start gap-2 font-medium hover:underline"
                            style={{ color: 'hsl(210, 100%, 45%)' }}
                            data-testid={`cg-location-${l.slug}`}
                          >
                            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {l.title}, {l.city}
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/kalender"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                      style={{ color: 'hsl(210, 100%, 45%)' }}
                      data-testid="cg-calendar-link"
                    >
                      Se datoer pr. lokation
                    </Link>
                  </div>
                );
              })()}

              {/* Contact */}
              <div className="bg-gradient-to-br from-[hsl(210,100%,45%)] to-[hsl(210,100%,35%)] rounded-xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-2">Har du spørgsmål?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Vi hjælper dig gerne med at finde det rette kursus.
                </p>
                <Link
                  to="/kontakt"
                  className="block w-full py-2 bg-white text-[hsl(210,100%,45%)] font-medium rounded-lg hover:bg-white/90 transition-colors text-center no-underline"
                  data-testid="cg-contact-link"
                >
                  Kontakt os
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegistrationModal && registrationSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Tilmeld dig kurset</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {registrationSession.name || courseGroup.title}
                  </p>
                </div>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {/* Session Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>{formatDate(registrationSession.date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span>{registrationSession.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{registrationSession.location}</span>
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-[hsl(210,100%,45%)]">
                    <span>{registrationSession.price?.toLocaleString('da-DK')} kr.</span>
                  </div>
                </div>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleRegistrationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <User className="inline h-4 w-4 mr-1" />
                    Fulde navn *
                  </label>
                  <input
                    type="text"
                    value={registrationForm.name}
                    onChange={(e) => setRegistrationForm({...registrationForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Indtast dit fulde navn"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={registrationForm.email}
                    onChange={(e) => setRegistrationForm({...registrationForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="din@email.dk"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={registrationForm.phone}
                    onChange={(e) => setRegistrationForm({...registrationForm, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+45 12 34 56 78"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    CPR-nummer *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={registrationForm.cpr_number}
                    onChange={(e) => setRegistrationForm({...registrationForm, cpr_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="DDMMÅÅ-XXXX"
                    required
                    data-testid="input-cpr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Building className="inline h-4 w-4 mr-1" />
                    Virksomhed (valgfrit)
                  </label>
                  <input
                    type="text"
                    value={registrationForm.company}
                    onChange={(e) => setRegistrationForm({...registrationForm, company: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Virksomhedsnavn"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bemærkninger (valgfrit)
                  </label>
                  <textarea
                    value={registrationForm.comments}
                    onChange={(e) => setRegistrationForm({...registrationForm, comments: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Eventuelle bemærkninger eller spørgsmål..."
                    rows={3}
                  />
                </div>

                {/* Price Summary */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-700">Total pris:</span>
                    <span className="text-xl font-bold text-[hsl(210,100%,45%)]">
                      {registrationSession.price?.toLocaleString('da-DK')} kr.
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Ved at tilmelde dig accepterer du vores kursusbetingelser. Faktura sendes til din email.
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    disabled={submitting}
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-[hsl(210,100%,45%)] text-white rounded-lg hover:bg-[hsl(210,100%,40%)] transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Sender...' : 'Bekræft tilmelding'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {courseGroup && (
        <CourseStructuredData group={courseGroup} sessions={sessions} faq={faqItems} />
      )}

      <Footer />
    </div>
  );
};

export default CourseGroupDetailPage;
